import { roundToPeso } from "./roundToPeso";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { tokenizeSearch } from "./andSearch"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Safely converts any value to a number.
 * Returns 0 if the value is null, undefined, NaN, or non-numeric string.
 */
export function toNum(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;

  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }

  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
}

/** IVA 21% — default de Producto.iva_porcentaje. Pedido proveedor: total = neto × 1,21. */
export const IVA_PEDIDO_PROVEEDOR_PCT = 21;

export function applyIvaPedidoProveedor(
  neto: number,
  ivaPct: number = IVA_PEDIDO_PROVEEDOR_PCT,
): number {
  return roundToPeso(neto * (1 + toNum(ivaPct) / 100));
}

export function pedidoProveedorLineaNetoSinIva(opts: {
  precioBase: number;
  cantidad: number;
  descuento1?: number;
  descuento2?: number;
}): number {
  let sub = roundToPeso(opts.precioBase) * toNum(opts.cantidad);
  const d1 = toNum(opts.descuento1);
  const d2 = toNum(opts.descuento2);
  if (d1 > 0) sub -= (sub * d1) / 100;
  if (d2 > 0) sub -= (sub * d2) / 100;
  return sub;
}

export function pedidoProveedorLineaTotal(opts: {
  precioBase: number;
  cantidad: number;
  descuento1?: number;
  descuento2?: number;
  ivaPct?: number;
}): number {
  return applyIvaPedidoProveedor(
    pedidoProveedorLineaNetoSinIva(opts),
    opts.ivaPct ?? IVA_PEDIDO_PROVEEDOR_PCT,
  );
}

/** Redondea a 2 decimales (centavos ARS). Valores no finitos → 0; |n| < 0.005 → 0. */
export function roundMoney(value: string | number | null | undefined): number {
  const n = toNum(value);
  if (!Number.isFinite(n)) return 0;
  const rounded = Math.round(n * 100) / 100;
  return Math.abs(rounded) < 0.005 ? 0 : rounded;
}

/** Parsea y redondea un monto monetario de forma segura. */
export function parseMoney(value: string | number | null | undefined): number {
  return roundMoney(value);
}

/** Formato AR con 2 decimales (sin símbolo $). */
export function formatMoneyAr(value: string | number | null | undefined): string {
  return roundMoney(value).toLocaleString('es-AR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** true si el valor es un monto finito y válido tras parseMoney. */
export function isFiniteMoney(value: string | number | null | undefined): boolean {
  const n = parseMoney(value);
  return Number.isFinite(n);
}

export type ProductSearchHighlight = {
  index: number;
  setIndex: (index: number) => void;
};

/** Índice visible en la lista de sugerencias. 0 si no hay matches. */
export function clampProductSearchHighlight(index: number, matchCount: number): number {
  if (matchCount <= 0) return 0;
  if (!Number.isFinite(index)) return 0;
  const i = Math.trunc(index);
  if (i < 0) return 0;
  if (i >= matchCount) return matchCount - 1;
  return i;
}

/** Flechas: cicla el resaltado (abajo +1, arriba −1). */
export function cycleProductSearchHighlight(
  index: number,
  matchCount: number,
  delta: number,
): number {
  if (matchCount <= 0) return 0;
  const cur = clampProductSearchHighlight(index, matchCount);
  const d = Math.trunc(delta);
  return ((cur + d) % matchCount + matchCount) % matchCount;
}

/**
 * Teclado del buscador de productos (solo en el input de búsqueda).
 * Tab: agrega solo si hay exactamente un match.
 * Enter: con highlight, agrega la fila resaltada; sin highlight, solo si hay un match.
 * Flechas: ciclan el resaltado si se pasa highlight. Enter nunca envía el formulario.
 */
export function handleProductSearchHotkey(
  e: { key: string; preventDefault: () => void },
  matchCount: number,
  onAdd: (index: number) => void,
  highlight?: ProductSearchHighlight | null,
): void {
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    if (!highlight || matchCount <= 0) return;
    e.preventDefault();
    const delta = e.key === "ArrowDown" ? 1 : -1;
    highlight.setIndex(cycleProductSearchHighlight(highlight.index, matchCount, delta));
    return;
  }
  if (e.key !== "Enter" && e.key !== "Tab") return;
  if (e.key === "Tab") {
    if (matchCount === 1) {
      e.preventDefault();
      onAdd(0);
    }
    return;
  }
  e.preventDefault();
  if (matchCount <= 0) return;
  if (highlight) {
    onAdd(clampProductSearchHighlight(highlight.index, matchCount));
    return;
  }
  if (matchCount === 1) {
    onAdd(0);
  }
}

type ProductSearchHit = {
  codigo?: string;
  nombre?: string;
  codigoProveedor?: string;
  codigo_proveedor?: string;
};

/**
 * Recorta resultados de la API al texto actual (evita Tab/Enter con debounce viejo).
 * Token-AND (máx. 4, cualquier orden, cada token en código / nombre / código proveedor)
 * alineado con FuzzySearchFilter. No exige la frase seguida.
 */
export function filterProductSearchMatches<T extends ProductSearchHit>(
  products: T[],
  query: string,
): T[] {
  const tokens = tokenizeSearch(query);
  if (tokens.length === 0) return [];
  return products.filter((p) => {
    const codigo = String(p.codigo || "").toLowerCase();
    const nombre = String(p.nombre || "").toLowerCase();
    const codigoProv = String(p.codigoProveedor || p.codigo_proveedor || "").toLowerCase();
    return tokens.every(
      (t) =>
        codigo.includes(t) ||
        nombre.includes(t) ||
        (codigoProv.length > 0 && codigoProv.includes(t)),
    );
  });
}

/**
 * Extrae YYYY-MM-DD de valores API (DateField como fecha o ISO con tiempo).
 * No usar `new Date("YYYY-MM-DD")` para mostrar: en zonas UTC− interpreta como medianoche UTC y resta un día al formatear local.
 */
export function toDateOnlyString(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/** DD/MM/YYYY para calendario (AR) sin interpretación timezone-unsafe. */
export function formatDateOnlyEsAR(raw: string | null | undefined): string {
  const ymd = toDateOnlyString(raw);
  if (!ymd) return "-";
  const [y, mo, d] = ymd.split("-");
  return `${d}/${mo}/${y}`;
}
